import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Space, Switch, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getTeamById,
  createTeam,
  updateTeam,
  TEAM_VISIBILITY,
} from '@/api/collaborationApi';
import { useAuth } from '@/hooks/useAuth';

export default function TeamForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEdit = !!id;
  const canWrite = hasPermission('collaboration', 'write');

  useEffect(() => {
    if (!canWrite) navigate('/collaboration/teams');
  }, [canWrite, navigate]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await getTeamById(id);
        if (res.data?.success && res.data.data) {
          form.setFieldsValue(res.data.data);
        }
      } catch {
        message.error(t('app.error'));
      }
    })();
  }, [id, form, t]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      if (isEdit) {
        await updateTeam(id!, values);
        message.success(t('collaboration.team.update_success'));
      } else {
        await createTeam(values);
        message.success(t('collaboration.team.create_success'));
      }
      navigate('/collaboration/teams');
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ visibility: 'private', is_active: true }}
      >
        <Form.Item
          name="name"
          label={t('collaboration.team.field.name')}
          rules={[{ required: true, max: 200 }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('collaboration.team.field.description')}
          rules={[{ max: 1000 }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item
          name="visibility"
          label={t('collaboration.team.field.visibility')}
        >
          <Select
            options={TEAM_VISIBILITY.map((v) => ({
              value: v,
              label: t(`collaboration.team.visibility_options.${v}`),
            }))}
          />
        </Form.Item>

        {isEdit && (
          <Form.Item
            name="is_active"
            label={t('collaboration.team.field.is_active')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        )}

        <Form.Item name="tags" label={t('collaboration.team.field.tags')}>
          <Select mode="tags" tokenSeparators={[',']} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEdit ? t('app.save') : t('app.create')}
            </Button>
            <Button onClick={() => navigate('/collaboration/teams')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
